import React from 'react';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const UpcomingEvents = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-cyan-50/30 to-violet-50/30">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-500 to-pink-500 text-white px-5 py-2 rounded-full mb-4 shadow-md">
              <Icon name="Calendar" size={15} color="white" />
              <span className="text-sm font-semibold tracking-wide">Upcoming Events</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground">
              Don't Miss These Events
            </h2>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Live fairs, expert webinars, and open days — all virtual, all free.
            </p>
          </div>
          <button
            onClick={() => navigate('/auditorium-webinar-hub')}
            className="inline-flex items-center gap-2 text-violet-600 font-semibold text-sm hover:gap-3 transition-all duration-200 flex-shrink-0"
          >
            View All Events
            <Icon name="ArrowRight" size={16} color="currentColor" />
          </button>
        </div>

        {/* CTA Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Webinars CTA */}
          <div className="relative bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group overflow-hidden">
            <div className="absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br from-violet-500 to-purple-500 opacity-10 rounded-full" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center shadow-md">
                  <Icon name="Video" size={20} color="white" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Webinars</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="w-2 h-2 rounded-full bg-violet-500" />
                    <span className="text-xs font-bold text-muted-foreground">Live & Upcoming</span>
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground mb-3 group-hover:text-violet-700 transition-colors">
                Expert Webinars & Live Sessions
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Join live expert talks, educational webinars, and recorded sessions from top counselors and institute representatives.
              </p>
              <button
                onClick={() => navigate('/auditorium-webinar-hub')}
                className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                Browse Webinars
                <Icon name="ArrowRight" size={14} color="white" />
              </button>
            </div>
          </div>

          {/* Exhibition Hall CTA */}
          <div className="relative bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group overflow-hidden">
            <div className="absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br from-cyan-500 to-blue-500 opacity-10 rounded-full" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-md">
                  <Icon name="Building2" size={20} color="white" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Virtual Fair</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-xs font-bold text-green-600">Open Now</span>
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground mb-3 group-hover:text-cyan-700 transition-colors">
                Virtual Exhibition Hall
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Explore virtual booths from top institutions. Visit campuses, chat with counselors, and discover your perfect program.
              </p>
              <button
                onClick={() => navigate('/student-registration')}
                className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                Register to Explore
                <Icon name="ArrowRight" size={14} color="white" />
              </button>
            </div>
          </div>

          {/* Medical & Health CTA */}
          <div className="relative bg-gradient-to-br from-pink-50 to-rose-50 border-2 border-pink-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group overflow-hidden">
            <div className="absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br from-pink-500 to-rose-500 opacity-10 rounded-full" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center shadow-md">
                  <Icon name="Heart" size={20} color="white" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Counseling</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="w-2 h-2 rounded-full bg-pink-500" />
                    <span className="text-xs font-bold text-muted-foreground">Book a Session</span>
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground mb-3 group-hover:text-pink-700 transition-colors">
                1-on-1 Video Counseling
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get personalized guidance from institute counselors via live video sessions. Ask questions and get real answers.
              </p>
              <button
                onClick={() => navigate('/student-registration')}
                className="flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                Book Session
                <Icon name="ArrowRight" size={14} color="white" />
              </button>
            </div>
          </div>

          {/* Campus Tour CTA */}
          <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group overflow-hidden">
            <div className="absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br from-amber-500 to-orange-500 opacity-10 rounded-full" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-md">
                  <Icon name="Compass" size={20} color="white" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Campus Tour</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-xs font-bold text-muted-foreground">360° Virtual</span>
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground mb-3 group-hover:text-amber-700 transition-colors">
                360° Campus Tours
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Explore campuses virtually with immersive 360-degree tours. Visit classrooms, labs, and facilities from anywhere.
              </p>
              <button
                onClick={() => navigate('/student-registration')}
                className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                Explore Campuses
                <Icon name="ArrowRight" size={14} color="white" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom banner */}
        <div className="mt-10 bg-gradient-to-r from-cyan-600 via-violet-600 to-amber-500 rounded-2xl p-6 md:p-8 text-white text-center shadow-xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-left">
              <p className="text-sm font-medium text-white/80 mb-1">Are you an institution?</p>
              <h3 className="text-xl md:text-2xl font-heading font-bold">Showcase Your Institution at StudyExpo</h3>
              <p className="text-white/80 text-sm mt-1">Reach thousands of students actively looking for admissions</p>
            </div>
            <button
              onClick={() => navigate('/institute-login')}
              className="flex-shrink-0 flex items-center gap-2 bg-white text-violet-700 px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            >
              <Icon name="Building2" size={18} color="currentColor" />
              Register Your Institute
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UpcomingEvents;
